import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Leaf, Eye, EyeSlash, ArrowLeft, Lock } from '@phosphor-icons/react';

export default function LoginPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [mustChangePassword, setMustChangePassword] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [changingPassword, setChangingPassword] = useState(false);
    const [tempToken, setTempToken] = useState(null);
    const { login, api } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email || !password) {
            toast.error('Please fill in all fields');
            return;
        }
        
        setLoading(true);
        try {
            const response = await api().post('/auth/login', { email, password });
            const { access_token, user: userData, must_change_password } = response.data;
            
            if (must_change_password) {
                setTempToken(access_token);
                setMustChangePassword(true);
                toast.info('You must set a new password before continuing');
            } else {
                localStorage.setItem('token', access_token);
                window.location.href = '/dashboard';
            }
        } catch (error) {
            console.error('Login error:', error);
            toast.error(error.response?.data?.detail || 'Invalid email or password');
        } finally {
            setLoading(false);
        }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        if (newPassword.length < 6) {
            toast.error('Password must be at least 6 characters');
            return;
        }
        if (newPassword !== confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }
        
        setChangingPassword(true);
        try {
            const instance = api();
            instance.defaults.headers.Authorization = `Bearer ${tempToken}`;
            await instance.post('/auth/change-password', { new_password: newPassword });
            toast.success('Password updated! Logging you in...');
            localStorage.setItem('token', tempToken);
            window.location.href = '/dashboard';
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to change password');
        } finally {
            setChangingPassword(false);
        }
    };

    if (mustChangePassword) {
        return (
            <div className="min-h-screen bg-[hsl(53,92%,96%)] dot-grid flex items-center justify-center p-4">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="w-full max-w-md"
                >
                    <div className="brutalist-card p-6 md:p-8">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 mx-auto rounded-full bg-[#f59e0b] border-4 border-[#1e293b] flex items-center justify-center mb-4">
                                <Lock weight="fill" className="text-white" size={32} />
                            </div>
                            <h1 className="font-fredoka text-2xl md:text-3xl font-bold text-[#1e293b]">
                                Set New Password
                            </h1>
                            <p className="text-[#475569] mt-2">
                                Your password has been reset. Please choose a new one.
                            </p>
                        </div>

                        <form onSubmit={handleChangePassword} className="space-y-5">
                            <div>
                                <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">
                                    New Password
                                </label>
                                <div className="relative">
                                    <input
                                        type={showNewPassword ? 'text' : 'password'}
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        className="brutalist-input w-full pr-12"
                                        placeholder="At least 6 characters"
                                        data-testid="new-password-input"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowNewPassword(!showNewPassword)}
                                        className="absolute right-4 top-1/2 -translate-y-1/2 text-[#475569] hover:text-[#1e293b]"
                                    >
                                        {showNewPassword ? <EyeSlash size={24} /> : <Eye size={24} />}
                                    </button>
                                </div>
                            </div>

                            <div>
                                <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">
                                    Confirm Password
                                </label>
                                <input
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className="brutalist-input w-full"
                                    placeholder="Re-enter your password"
                                    data-testid="confirm-password-input"
                                />
                            </div>

                            <motion.button
                                type="submit"
                                disabled={changingPassword}
                                whileHover={{ y: -2, x: -2 }}
                                whileTap={{ y: 2, x: 2 }}
                                className="brutalist-btn w-full bg-[#f59e0b] text-white text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                                data-testid="change-password-submit"
                            >
                                {changingPassword ? (
                                    <span className="flex items-center justify-center gap-2">
                                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        Updating...
                                    </span>
                                ) : (
                                    'Set New Password'
                                )}
                            </motion.button>
                        </form>
                    </div>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[hsl(53,92%,96%)] dot-grid flex items-center justify-center p-4">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full max-w-md"
            >
                {/* Back Link */}
                <Link 
                    to="/" 
                    className="inline-flex items-center gap-2 text-[#475569] hover:text-[#22c55e] mb-6 font-semibold"
                    data-testid="back-to-home"
                >
                    <ArrowLeft weight="bold" size={20} />
                    Back to Home
                </Link>
                
                <div className="brutalist-card p-6 md:p-8">
                    {/* Logo */}
                    <div className="text-center mb-8">
                        <div className="w-16 h-16 mx-auto rounded-full bg-[#22c55e] border-4 border-[#1e293b] flex items-center justify-center mb-4">
                            <Leaf weight="fill" className="text-white" size={32} />
                        </div>
                        <h1 className="font-fredoka text-2xl md:text-3xl font-bold text-[#1e293b]">
                            Welcome Back!
                        </h1>
                        <p className="text-[#475569] mt-2">
                            Log in to continue your adventure
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div>
                            <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">
                                Email Address
                            </label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="brutalist-input w-full"
                                placeholder="parent@email.com"
                                data-testid="login-email"
                            />
                        </div>

                        <div>
                            <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">
                                Password
                            </label>
                            <div className="relative">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="brutalist-input w-full pr-12"
                                    placeholder="Enter your password"
                                    data-testid="login-password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-[#475569] hover:text-[#1e293b]"
                                    data-testid="toggle-password"
                                >
                                    {showPassword ? <EyeSlash size={24} /> : <Eye size={24} />}
                                </button>
                            </div>
                            <div className="mt-2 text-right">
                                <Link
                                    to="/forgot-password"
                                    className="text-sm text-[#22c55e] font-semibold hover:underline"
                                    data-testid="forgot-password-link"
                                >
                                    Forgot Password?
                                </Link>
                            </div>
                        </div>

                        <motion.button
                            type="submit"
                            disabled={loading}
                            whileHover={{ y: -2, x: -2 }}
                            whileTap={{ y: 2, x: 2 }}
                            className="brutalist-btn w-full bg-[#22c55e] text-white text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                            data-testid="login-submit"
                        >
                            {loading ? (
                                <span className="flex items-center justify-center gap-2">
                                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    Logging in...
                                </span>
                            ) : (
                                'Log In'
                            )}
                        </motion.button>
                    </form>

                    <div className="mt-6 text-center">
                        <p className="text-[#475569]">
                            Don't have an account?{' '}
                            <Link 
                                to="/register" 
                                className="text-[#22c55e] font-semibold hover:underline"
                                data-testid="register-link"
                            >
                                Join Now
                            </Link>
                        </p>
                    </div>
                </div>
            </motion.div>
        </div>
    );
}
